import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { VedilooConnectStatus } from "@/components/VedilooConnectStatus";
import { FicSettingsCard } from "@/components/FicSettingsCard";
import { StripeSettingsCard } from "@/components/StripeSettingsCard";
import { VenergiaKeysCard } from "@/components/VenergiaKeysCard";
import { NetworkDiagnostics } from "@/pages/AdminGreenPower";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { MessageSquare, Mail, KeyRound, Percent, Plug, CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";

export default function AdminIntegrations({ section = "all" }) {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "diagnostica");
  const show = (s) => (section === "all" ? tab === s : section === s);
  const TABS = [["diagnostica", "Diagnostica"], ["piattaforma", "Piattaforma"], ["notifiche", "Notifiche & 2FA"], ["fatture", "Fatture in Cloud"], ["stripe", "Stripe"], ["venergia", "Venergia"], ["openapi", "OpenAPI"], ["reloadly", "Reloadly"], ["airalo", "Airalo eSIM"], ["spedizioni", "Spedizioni"]];
  const SECTION_TITLES = { reloadly: "Reloadly — Ricariche & Gift Card", openapi: "OpenAPI — Servizi digitali", airalo: "Airalo — eSIM", spedizioni: "Spedizioni — Vediloo" };
  const [form, setForm] = useState({
    twilio_account_sid: "", twilio_auth_token: "", twilio_from_number: "", twilio_verify_service_sid: "",
    sms_enabled: false, email_notifications_enabled: true, twofa_roles: [],
  });
  const [tokenSet, setTokenSet] = useState(false);
  const [saving, setSaving] = useState(false);
  const [demoAccess, setDemoAccess] = useState(false);
  const [mapEnabled, setMapEnabled] = useState(false);
  const [requireVisura, setRequireVisura] = useState(false);
  const [autoExpiry, setAutoExpiry] = useState(false);
  const [docFlags, setDocFlags] = useState({ require_doc_pec: false, require_doc_raccomandata: false, require_doc_bollettini: false, require_doc_sim: false });
  const [resetNotify, setResetNotify] = useState("both");

  const [pricing, setPricing] = useState({ openapi_markup_percent: 10, openapi_mode: "mock", openapi_env: "production", openapi_token_set: false });
  const [markupInput, setMarkupInput] = useState("10");
  const [services, setServices] = useState([]);
  const [savingPricing, setSavingPricing] = useState(false);

  const [oa, setOa] = useState({ username: "", token_set: false, oauth_url: "", mode: "mock", services: {} });
  const [oaToken, setOaToken] = useState("");
  const [savingOa, setSavingOa] = useState(false);
  const [testing, setTesting] = useState("");
  const [testResults, setTestResults] = useState({});

  const loadOpenApi = () => api.get("/settings/openapi").then((r) => setOa(r.data)).catch(() => {});

  const setSvc = (key, field, value) => setOa((o) => ({ ...o, services: { ...o.services, [key]: { ...o.services[key], [field]: value } } }));

  const saveOpenApi = async () => {
    setSavingOa(true);
    try {
      const payload = { username: oa.username, oauth_url: oa.oauth_url, mode: oa.mode, services: oa.services };
      if (oaToken) payload.token = oaToken;
      await api.put("/settings/openapi", payload);
      setOaToken("");
      toast.success("Configurazione OpenAPI salvata");
      loadOpenApi(); loadPricing();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio");
    } finally { setSavingOa(false); }
  };

  const testService = async (key) => {
    setTesting(key);
    try {
      const { data } = await api.post("/openapi/test", { service: key });
      setTestResults((t) => ({ ...t, [key]: data }));
      if (data.ok) toast.success(`${oa.services[key]?.label || key}: connessione OK`);
      else toast.error(`${oa.services[key]?.label || key}: ${data.detail || "test fallito"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore test");
    } finally { setTesting(""); }
  };

  const [air, setAir] = useState({ client_id: "", secret_set: false, base_url: "", mode: "sandbox", markup_percent: 15, configured: false });
  const [airSecret, setAirSecret] = useState("");
  const [savingAir, setSavingAir] = useState(false);
  const [testingAir, setTestingAir] = useState(false);
  const [airTest, setAirTest] = useState(null);
  const [syncingAir, setSyncingAir] = useState(false);

  const loadAiralo = () => api.get("/settings/airalo").then((r) => setAir(r.data)).catch(() => {});

  const saveAiralo = async () => {
    setSavingAir(true);
    try {
      const p = { client_id: air.client_id, base_url: air.base_url, mode: air.mode, markup_percent: parseFloat(air.markup_percent) || 0, pricing_tiers: air.pricing_tiers || [] };
      if (airSecret) p.client_secret = airSecret;
      await api.put("/settings/airalo", p);
      setAirSecret("");
      toast.success("Configurazione Airalo salvata");
      loadAiralo();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSavingAir(false); }
  };

  const testAiralo = async () => {
    setTestingAir(true);
    try {
      const { data } = await api.post("/airalo/test");
      setAirTest(data);
      data.ok ? toast.success(data.message) : toast.error(data.detail || "Test fallito");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore test"); }
    finally { setTestingAir(false); }
  };

  const syncAiralo = async () => {
    setSyncingAir(true);
    try {
      const { data } = await api.post("/airalo/sync-packages");
      toast.success(`Sincronizzati ${data.count} pacchetti eSIM`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore sincronizzazione"); }
    finally { setSyncingAir(false); }
  };

  const addTier = () => setAir((a) => ({ ...a, pricing_tiers: [...(a.pricing_tiers || []), { up_to: "", markup_percent: "" }] }));
  const removeTier = (i) => setAir((a) => ({ ...a, pricing_tiers: (a.pricing_tiers || []).filter((_, x) => x !== i) }));
  const updateTier = (i, k, v) => setAir((a) => ({ ...a, pricing_tiers: (a.pricing_tiers || []).map((t, x) => x === i ? { ...t, [k]: v === "" ? "" : parseFloat(v) } : t) }));

  const [ved, setVed] = useState({ api_key: "", secret_set: false, base_url: "", configured: false });
  const [vedSecret, setVedSecret] = useState("");
  const [savingVed, setSavingVed] = useState(false);
  const [testingVed, setTestingVed] = useState(false);
  const [vedTest, setVedTest] = useState(null);
  const loadVediloo = () => api.get("/settings/vediloo").then((r) => setVed(r.data)).catch(() => {});
  const saveVediloo = async () => {
    setSavingVed(true);
    try {
      const p = { api_key: ved.api_key, base_url: ved.base_url };
      if (vedSecret) p.secret = vedSecret;
      await api.put("/settings/vediloo", p);
      setVedSecret(""); toast.success("Credenziali Vediloo salvate"); loadVediloo();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSavingVed(false); }
  };
  const testVediloo = async () => {
    setTestingVed(true);
    try {
      const { data } = await api.post("/vediloo/test");
      setVedTest(data);
      data.ok ? toast.success(data.message) : toast.error(data.detail || data.message || "Test fallito");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore test"); }
    finally { setTestingVed(false); }
  };

  const [rel, setRel] = useState({ mode: "sandbox", client_id: "", client_secret_set: false, webhook_secret_set: false, webhook_url: "", products: { airtime: true, giftcard: true }, configured: false });
  const [relSecret, setRelSecret] = useState("");
  const [relHook, setRelHook] = useState("");
  const [savingRel, setSavingRel] = useState(false);
  const [testingRel, setTestingRel] = useState(false);
  const [relTest, setRelTest] = useState(null);
  const loadReloadly = () => api.get("/settings/reloadly").then((r) => setRel(r.data)).catch(() => {});
  const saveReloadly = async () => {
    setSavingRel(true);
    try {
      const p = { mode: rel.mode, client_id: rel.client_id, products: rel.products, webhook_url: rel.webhook_url || "" };
      if (relSecret) p.client_secret = relSecret;
      if (relHook) p.webhook_secret = relHook;
      await api.put("/settings/reloadly", p);
      setRelSecret(""); setRelHook(""); toast.success("Credenziali Reloadly salvate"); loadReloadly();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSavingRel(false); }
  };
  const testReloadly = async () => {
    setTestingRel(true);
    try {
      const { data } = await api.post("/reloadly/test");
      setRelTest(data);
      data.ok ? toast.success(data.message) : toast.error(data.detail || data.message || "Test fallito");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore test"); }
    finally { setTestingRel(false); }
  };

  const SYSTEMS = [
    { key: "twilio", label: "Twilio — SMS & OTP" },
    { key: "openapi", label: "OpenAPI.it" },
    { key: "reloadly", label: "Reloadly" },
    { key: "airalo", label: "Airalo eSIM" },
    { key: "stripe", label: "Stripe" },
    { key: "email", label: "Email (Resend)" },
    { key: "photon", label: "Photon (Geo)" },
  ];
  const [diag, setDiag] = useState({});
  const [diagBusy, setDiagBusy] = useState("");

  const testOne = async (key) => {
    setDiagBusy(key);
    try {
      const { data } = await api.post("/integrations/test", { system: key });
      setDiag((d) => ({ ...d, [key]: data }));
      data.ok ? toast.success(`${key}: OK`) : toast.error(`${key}: ${data.detail || "errore"}`);
    } catch (err) {
      setDiag((d) => ({ ...d, [key]: { ok: false, detail: formatApiErrorDetail(err.response?.data?.detail) || "Errore test" } }));
    } finally { setDiagBusy(""); }
  };

  const testAllSystems = async () => {
    setDiagBusy("all");
    try {
      const { data } = await api.post("/integrations/test", { system: "all" });
      setDiag(data.results || {});
      const failed = Object.entries(data.results || {}).filter(([, v]) => !v.ok).map(([k]) => k);
      failed.length ? toast.error(`Problemi: ${failed.join(", ")}`) : toast.success("Tutti i sistemi rispondono correttamente");
    } catch (err) {
      toast.error("Errore durante il test");
    } finally { setDiagBusy(""); }
  };

  const load = () => api.get("/settings/integrations").then((r) => {
    setForm((f) => ({
      ...f,
      twilio_account_sid: r.data.twilio_account_sid || "",
      twilio_from_number: r.data.twilio_from_number || "",
      twilio_verify_service_sid: r.data.twilio_verify_service_sid || "",
      twilio_auth_token: "",
      sms_enabled: r.data.sms_enabled,
      email_notifications_enabled: r.data.email_notifications_enabled,
      twofa_roles: r.data.twofa_roles || [],
    }));
    setTokenSet(r.data.twilio_auth_token_set);
  }).catch(() => {});

  const loadPricing = () => {
    api.get("/settings/pricing").then((r) => { setPricing(r.data); setMarkupInput(String(r.data.openapi_markup_percent)); }).catch(() => {});
    api.get("/openapi/services").then((r) => setServices(r.data.services || [])).catch(() => {});
  };

  useEffect(() => { load(); loadPricing(); loadOpenApi(); loadAiralo(); loadVediloo(); loadReloadly(); api.get("/settings/features").then((r) => { setDemoAccess(r.data.demo_access_enabled); setMapEnabled(r.data.dealer_map_enabled); setRequireVisura(r.data.require_visura_azienda); setAutoExpiry(r.data.auto_expiry_reminders_enabled); setDocFlags({ require_doc_pec: !!r.data.require_doc_pec, require_doc_raccomandata: !!r.data.require_doc_raccomandata, require_doc_bollettini: !!r.data.require_doc_bollettini, require_doc_sim: !!r.data.require_doc_sim }); setResetNotify(r.data.reset_notify_target || "both"); }).catch(() => {}); }, []);

  const toggleAutoExpiry = async (v) => {
    setAutoExpiry(v);
    try { await api.put("/settings/features", { auto_expiry_reminders_enabled: v }); toast.success(v ? "Promemoria automatici settimanali attivati" : "Promemoria automatici disattivati"); }
    catch (err) { setAutoExpiry(!v); toast.error("Errore salvataggio"); }
  };

  const toggleRequireVisura = async (v) => {
    setRequireVisura(v);
    try { await api.put("/settings/features", { require_visura_azienda: v }); toast.success(v ? "Visura obbligatoria per Azienda attivata" : "Visura non più obbligatoria"); }
    catch (err) { setRequireVisura(!v); toast.error("Errore salvataggio"); }
  };

  const toggleDemo = async (v) => {
    setDemoAccess(v);
    try { await api.put("/settings/features", { demo_access_enabled: v }); toast.success(v ? "Accesso demo abilitato" : "Accesso demo disabilitato"); }
    catch (err) { setDemoAccess(!v); toast.error("Errore salvataggio"); }
  };

  const toggleMap = async (v) => {
    setMapEnabled(v);
    try { await api.put("/settings/features", { dealer_map_enabled: v }); toast.success(v ? "Mappa dealer pubblicata" : "Mappa dealer nascosta"); }
    catch (err) { setMapEnabled(!v); toast.error("Errore salvataggio"); }
  };

  const toggleDocFlag = async (key, v) => {
    setDocFlags((f) => ({ ...f, [key]: v }));
    try { await api.put("/settings/features", { [key]: v }); toast.success(v ? "Documento reso obbligatorio" : "Documento reso opzionale"); }
    catch (err) { setDocFlags((f) => ({ ...f, [key]: !v })); toast.error("Errore salvataggio"); }
  };

  const setResetNotifyTarget = async (v) => {
    const prev = resetNotify;
    setResetNotify(v);
    try { await api.put("/settings/features", { reset_notify_target: v }); toast.success("Destinatari notifica reset aggiornati"); }
    catch (err) { setResetNotify(prev); toast.error("Errore salvataggio"); }
  };

  const save = async (e) => {
    e.preventDefault();
    const vsid = (form.twilio_verify_service_sid || "").trim();
    if (vsid && !/^VA[0-9a-f]{32}$/i.test(vsid)) {
      toast.error("Il Verify Service SID sembra incompleto: deve iniziare con 'VA' e avere 34 caratteri. Ricontrolla di averlo incollato per intero.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.twilio_auth_token) delete payload.twilio_auth_token;
      await api.put("/settings/integrations", payload);
      toast.success("Impostazioni salvate");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await api.put("/settings/pricing", { openapi_markup_percent: parseFloat(markupInput) || 0 });
      toast.success("Ricarico aggiornato, prezzi ricalcolati");
      loadPricing();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio");
    } finally { setSavingPricing(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-integrations-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Configurazione</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">{section === "all" ? "Integrazioni & Notifiche" : SECTION_TITLES[section]}</h1>
        <p className="text-sm text-muted-foreground mt-2">{section === "all" ? "Configura i canali di notifica e la rivendita dei servizi OpenAPI." : "Credenziali, test di connessione e opzioni per questo fornitore."}</p>
      </div>

      {section === "all" && <div className="sticky top-0 z-10 -mx-1 px-1 py-2 bg-background/90 backdrop-blur flex flex-wrap gap-1.5" data-testid="integrations-tabs">
        {TABS.map(([k, l]) => <Button key={k} type="button" size="sm" variant={tab === k ? "default" : "outline"} onClick={() => setTab(k)} data-testid={`integrations-tab-${k}`}>{l}</Button>)}
      </div>}

      {show("diagnostica") && <VedilooConnectStatus />}

      {show("diagnostica") && (
      <Card className="p-6 glow-border space-y-4" data-testid="diagnostics-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Plug className="w-5 h-5" /></div>
          <div>
            <h2 className="font-head text-lg font-semibold">Diagnostica connessioni</h2>
            <p className="text-xs text-muted-foreground">Verifica che ogni sistema risponda. Test di sola lettura: nessun SMS o pagamento reale.</p>
          </div>
          <Button type="button" size="sm" className="ml-auto gap-2" disabled={!!diagBusy} onClick={testAllSystems} data-testid="diag-test-all-button">
            {diagBusy === "all" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Testa tutto
          </Button>
        </div>
        <div className="space-y-2">
          {SYSTEMS.map((s) => {
            const r = diag[s.key];
            return (
              <div key={s.key} className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3" data-testid={`diag-row-${s.key}`}>
                <span className="font-medium text-sm w-full sm:w-40 shrink-0">{s.label}</span>
                {r ? (
                  <Badge className={r.ok ? "bg-teal-600 hover:bg-teal-600" : ""} variant={r.ok ? undefined : "destructive"} data-testid={`diag-badge-${s.key}`}>
                    {r.ok ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}{r.ok ? "OK" : "Errore"}
                  </Badge>
                ) : <Badge variant="outline">—</Badge>}
                <span className="text-xs text-muted-foreground truncate flex-1" data-testid={`diag-msg-${s.key}`}>{r ? `${r.detail || ""}${r.latency_ms != null ? ` (${r.latency_ms}ms)` : ""}` : ""}</span>
                <Button type="button" size="sm" variant="outline" disabled={!!diagBusy} onClick={() => testOne(s.key)} data-testid={`diag-test-${s.key}`}>
                  {diagBusy === s.key ? <Loader2 className="w-4 h-4 animate-spin" /> : "Testa"}
                </Button>
              </div>
            );
          })}
        </div>
      </Card>
      )}

      {show("piattaforma") && (
      <Card className="p-6 glow-border" data-testid="demo-access-settings-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-head text-lg font-semibold">Accesso ambienti demo</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Se attivo, mostra i pulsanti di accesso rapido demo (Admin/Dealer/Cliente) nella pagina di login.</p>
          </div>
          <Switch checked={demoAccess} onCheckedChange={toggleDemo} data-testid="demo-access-switch" />
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div>
            <h2 className="font-head text-lg font-semibold">Mappa dealer pubblica</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Se attivo, la pagina pubblica /dealers mostra i dealer sulla mappa. Tienila OFF finché non hai abbastanza dealer.</p>
          </div>
          <Switch checked={mapEnabled} onCheckedChange={toggleMap} data-testid="dealer-map-switch" />
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div>
            <h2 className="font-head text-lg font-semibold">Visura obbligatoria per Azienda</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Se attivo, i dealer non possono salvare un cliente di tipo "Azienda" senza aver caricato la visura camerale.</p>
          </div>
          <Switch checked={requireVisura} onCheckedChange={toggleRequireVisura} data-testid="require-visura-switch" />
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div>
            <h2 className="font-head text-lg font-semibold">Promemoria automatici documenti in scadenza</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Se attivo, ogni lunedì mattina invia in automatico i solleciti per i documenti amministratore in scadenza entro 60 giorni. L'invio manuale dalla pagina "Documenti in scadenza" resta sempre disponibile.</p>
          </div>
          <Switch checked={autoExpiry} onCheckedChange={toggleAutoExpiry} data-testid="auto-expiry-switch" />
        </div>
        <div className="mt-4 pt-4 border-t border-border">
          <h2 className="font-head text-lg font-semibold">Documento obbligatorio per processo</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Se attivo, il processo richiede il caricamento del documento del cliente (o della visura per le aziende) prima di procedere. La compilazione automatica dei dati anagrafici resta sempre disponibile anche quando è opzionale.</p>
          <div className="mt-3 space-y-2">
            {[
              ["require_doc_pec", "PEC — Posta Certificata"],
              ["require_doc_raccomandata", "Raccomandata"],
              ["require_doc_bollettini", "Bollettini"],
              ["require_doc_sim", "Vendi SIM / Italia Mobile"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <span className="text-sm">{label}</span>
                <Switch checked={docFlags[key]} onCheckedChange={(v) => toggleDocFlag(key, v)} data-testid={`doc-required-switch-${key}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-border">
          <h2 className="font-head text-lg font-semibold">Notifica reset password</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Quando viene inviato un reset password, avvisa (senza link) questi destinatari.</p>
          <div className="mt-3 inline-flex flex-wrap max-w-full gap-1 rounded-lg border border-border p-0.5">
            {[["admin", "Solo Admin"], ["master", "Solo Master"], ["both", "Admin + Master"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setResetNotifyTarget(v)} className={`px-3 py-1.5 rounded-md text-sm transition-colors ${resetNotify === v ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid={`reset-notify-${v}`}>{l}</button>
            ))}
          </div>
        </div>
      </Card>
      )}

      {show("openapi") && (<>
      <Card className="p-6 glow-border space-y-4" data-testid="openapi-pricing-card">
        <div className="flex flex-wrap items-center gap-3 [&>div]:min-w-0">
          <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Percent className="w-5 h-5" /></div>
          <div>
            <h2 className="font-head text-lg font-semibold">Rivendita Servizi OpenAPI</h2>
            <p className="text-xs text-muted-foreground">Il prezzo di vendita = costo OpenAPI (netto) + ricarico % + IVA 22%. Il ricarico di ogni servizio si imposta in Prezzi Servizi; questo vale solo per i servizi senza ricarico proprio. Il guadagno si calcola al netto dell'IVA (recuperabile).</p>
          </div>
          <div className="ml-auto flex flex-wrap min-w-0 max-w-full gap-2">
            <Badge variant="outline" className="uppercase">{pricing.openapi_mode}</Badge>
            <Badge className={pricing.openapi_token_set ? "bg-teal-600 hover:bg-teal-600" : ""} variant={pricing.openapi_token_set ? undefined : "outline"}>{pricing.openapi_token_set ? "Token OK" : "Token mancante"}</Badge>
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <Label>Ricarico rivendita (%)</Label>
            <Input type="number" step="0.1" min="0" className="mt-1.5" data-testid="markup-input" value={markupInput} onChange={(e) => setMarkupInput(e.target.value)} />
          </div>
          <Button onClick={savePricing} disabled={savingPricing} data-testid="markup-save-button">{savingPricing ? "Salvataggio…" : "Salva e ricalcola"}</Button>
        </div>
        {services.length > 0 && (
          <Table>
            <TableHeader><TableRow><TableHead>Servizio</TableHead><TableHead>Costo</TableHead><TableHead>Prezzo vendita</TableHead><TableHead>Margine</TableHead></TableRow></TableHeader>
            <TableBody>
              {services.map((s) => (
                <TableRow key={s.id} data-testid={`openapi-service-row-${s.openapi_service_id || s.id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell>{euro(s.cost)}</TableCell>
                  <TableCell className="font-semibold">{s.variable_amount ? "—" : euro(s.price)}</TableCell>
                  <TableCell className="text-teal-500">{s.margin == null ? "—" : euro(s.margin)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* OpenAPI servizi live */}
      <Card className="p-6 glow-border space-y-5" data-testid="openapi-services-card">
        <div className="flex flex-wrap items-center gap-3 [&>div]:min-w-0">
          <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Plug className="w-5 h-5" /></div>
          <div>
            <h2 className="font-head text-lg font-semibold">Servizi OpenAPI — Configurazione Live</h2>
            <p className="text-xs text-muted-foreground">Inserisci credenziali, host e scope di ogni servizio, poi premi "Testa" per verificare la connessione reale.</p>
          </div>
          <Badge variant="outline" className="ml-auto uppercase">{oa.mode}</Badge>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Username OpenAPI</Label>
            <Input className="mt-1.5 font-mono" data-testid="openapi-username-input" value={oa.username || ""}
              onChange={(e) => setOa({ ...oa, username: e.target.value })} placeholder="email dell'account OpenAPI" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Token (API key) {oa.token_set && <span className="text-xs text-teal-500">(salvato)</span>}</Label>
            <Input type="password" className="mt-1.5 font-mono" data-testid="openapi-token-input" value={oaToken}
              onChange={(e) => setOaToken(e.target.value)} placeholder={oa.token_set ? "••••••• (lascia vuoto per non modificare)" : "Il tuo token OpenAPI"} />
          </div>
          <div>
            <Label>OAuth URL</Label>
            <Input className="mt-1.5 font-mono" data-testid="openapi-oauth-input" value={oa.oauth_url || ""}
              onChange={(e) => setOa({ ...oa, oauth_url: e.target.value })} placeholder="https://oauth.openapi.it/token" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border px-3 mt-6 sm:mt-0 sm:self-end sm:h-[42px]">
            <Label>Modalità Live</Label>
            <Switch checked={oa.mode === "live"} onCheckedChange={(v) => setOa({ ...oa, mode: v ? "live" : "mock" })} data-testid="openapi-mode-switch" />
          </div>
        </div>

        <div className="space-y-3">
          {Object.entries(oa.services || {}).map(([key, s]) => {
            const res = testResults[key];
            return (
              <div key={key} className="rounded-xl border border-border p-4 space-y-3" data-testid={`openapi-service-config-${key}`}>
                <div className="flex items-center gap-3">
                  <Switch checked={!!s.enabled} onCheckedChange={(v) => setSvc(key, "enabled", v)} data-testid={`openapi-enable-${key}`} />
                  <span className="font-semibold">{s.label}</span>
                  {res && (
                    <Badge className={`ml-auto ${res.ok ? "bg-teal-600 hover:bg-teal-600" : ""}`} variant={res.ok ? undefined : "destructive"} data-testid={`openapi-test-badge-${key}`}>
                      {res.ok ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                      {res.ok ? "Connessione OK" : "Errore"}
                    </Badge>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Host / Base URL</Label>
                    <Input className="mt-1 font-mono text-sm" data-testid={`openapi-baseurl-${key}`} value={s.base_url || ""}
                      onChange={(e) => setSvc(key, "base_url", e.target.value)} placeholder="es. https://ws.pagasubito.it" />
                  </div>
                  <div>
                    <Label className="text-xs">Scope OAuth</Label>
                    <Input className="mt-1 font-mono text-sm" data-testid={`openapi-scope-${key}`} value={s.scope || ""}
                      onChange={(e) => setSvc(key, "scope", e.target.value)} placeholder="es. GET:ws.pagasubito.it/pay" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button type="button" variant="outline" size="sm" disabled={testing === key} onClick={() => testService(key)} data-testid={`openapi-test-${key}`}>
                    {testing === key ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Test…</> : "Testa connessione"}
                  </Button>
                  {res && <p className={`text-xs ${res.ok ? "text-teal-500" : "text-red-400"}`} data-testid={`openapi-test-msg-${key}`}>{res.message || res.detail}</p>}
                </div>
              </div>
            );
          })}
        </div>

        <Button onClick={saveOpenApi} disabled={savingOa} data-testid="openapi-save-button">{savingOa ? "Salvataggio…" : "Salva configurazione OpenAPI"}</Button>
      </Card>
      </>)}

      {show("airalo") && (
      <Card className="p-6 glow-border space-y-5" data-testid="airalo-card">
        <div className="flex flex-wrap items-center gap-3 [&>div]:min-w-0">
          <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Plug className="w-5 h-5" /></div>
          <div>
            <h2 className="font-head text-lg font-semibold">eSIM Airalo — Configurazione</h2>
            <p className="text-xs text-muted-foreground">Inserisci le credenziali Partner Airalo, testa la connessione e sincronizza il catalogo pacchetti.</p>
          </div>
          <div className="ml-auto flex flex-wrap min-w-0 max-w-full gap-2">
            <Badge variant="outline" className="uppercase">{air.mode}</Badge>
            <Badge className={air.configured ? "bg-teal-600 hover:bg-teal-600" : ""} variant={air.configured ? undefined : "outline"}>{air.configured ? "Configurato" : "Non configurato"}</Badge>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Client ID</Label>
            <Input className="mt-1.5 font-mono" data-testid="airalo-clientid-input" value={air.client_id || ""} onChange={(e) => setAir({ ...air, client_id: e.target.value })} placeholder="client_id Airalo" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Client Secret {air.secret_set && <span className="text-xs text-teal-500">(salvato)</span>}</Label>
            <Input type="password" className="mt-1.5 font-mono" data-testid="airalo-secret-input" value={airSecret} onChange={(e) => setAirSecret(e.target.value)} placeholder={air.secret_set ? "••••••• (lascia vuoto per non modificare)" : "client_secret Airalo"} />
          </div>
          <div>
            <Label>Base URL</Label>
            <Input className="mt-1.5 font-mono" data-testid="airalo-baseurl-input" value={air.base_url || ""} onChange={(e) => setAir({ ...air, base_url: e.target.value })} placeholder="https://partners-api.airalo.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Ricarico %</Label>
              <Input type="number" step="0.1" min="0" className="mt-1.5" data-testid="airalo-markup-input" value={air.markup_percent} onChange={(e) => setAir({ ...air, markup_percent: e.target.value })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-3 self-end h-[42px]">
              <Label className="text-xs">Produzione</Label>
              <Switch checked={air.mode === "production"} onCheckedChange={(v) => setAir({ ...air, mode: v ? "production" : "sandbox" })} data-testid="airalo-mode-switch" />
            </div>
          </div>
          <div className="rounded-lg border border-border p-4 space-y-3" data-testid="airalo-tiers">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-semibold">Fasce di ricarico dinamiche</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Ricarico % per fascia di costo netto. Se vuote, si applica il ricarico base sopra.</p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addTier} data-testid="airalo-tier-add">+ Fascia</Button>
            </div>
            {(air.pricing_tiers || []).map((t, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2" data-testid={`airalo-tier-${i}`}>
                <div>
                  <Label className="text-xs">Costo fino a € (vuoto = oltre)</Label>
                  <Input type="number" step="0.01" min="0" className="mt-1" value={t.up_to ?? ""} onChange={(e) => updateTier(i, "up_to", e.target.value)} placeholder="es. 10" data-testid={`airalo-tier-upto-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Ricarico %</Label>
                  <Input type="number" step="0.1" min="0" className="mt-1" value={t.markup_percent ?? ""} onChange={(e) => updateTier(i, "markup_percent", e.target.value)} placeholder="es. 40" data-testid={`airalo-tier-markup-${i}`} />
                </div>
                <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => removeTier(i)} data-testid={`airalo-tier-del-${i}`}>Rimuovi</Button>
              </div>
            ))}
            {(air.pricing_tiers || []).length === 0 && <p className="text-xs text-muted-foreground">Nessuna fascia: ricarico fisso {air.markup_percent}% su tutti i pacchetti.</p>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={saveAiralo} disabled={savingAir} data-testid="airalo-save-button">{savingAir ? "Salvataggio…" : "Salva credenziali"}</Button>
          <Button type="button" variant="outline" disabled={testingAir} onClick={testAiralo} data-testid="airalo-test-button">
            {testingAir ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Test…</> : "Testa connessione"}
          </Button>
          <Button type="button" variant="outline" disabled={syncingAir} onClick={syncAiralo} data-testid="airalo-sync-button">
            {syncingAir ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sync…</> : "Sincronizza pacchetti"}
          </Button>
          {airTest && (
            <span className={`text-xs flex items-center gap-1 ${airTest.ok ? "text-teal-500" : "text-red-400"}`} data-testid="airalo-test-msg">
              {airTest.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {airTest.message || airTest.detail}
            </span>
          )}
        </div>
      </Card>
      )}

      {show("spedizioni") && (
      <Card className="p-6 glow-border space-y-4" data-testid="vediloo-card">
        <div className="flex flex-wrap items-center gap-3 [&>div]:min-w-0">
          <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Plug className="w-5 h-5" /></div>
          <div>
            <h2 className="font-head text-lg font-semibold">Vediloo — Spedizioni (Partner API)</h2>
            <p className="text-xs text-muted-foreground">Integra facilmente le spedizioni nella tua piattaforma e sfrutta il tuo credito prepagato. Auth HMAC-SHA256.</p>
          </div>
          <Badge className={ved.configured ? "bg-teal-600 hover:bg-teal-600 ml-auto" : "ml-auto"} variant={ved.configured ? undefined : "outline"}>{ved.configured ? "Configurato" : "Secret mancante"}</Badge>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>API Key (X-Vediloo-Key)</Label><Input className="mt-1.5 font-mono" data-testid="vediloo-apikey-input" value={ved.api_key || ""} onChange={(e) => setVed({ ...ved, api_key: e.target.value })} placeholder="vk_live_…" /></div>
          <div><Label className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Secret (HMAC) {ved.secret_set && <span className="text-xs text-teal-500">(salvato)</span>}</Label><Input type="password" className="mt-1.5 font-mono" data-testid="vediloo-secret-input" value={vedSecret} onChange={(e) => setVedSecret(e.target.value)} placeholder={ved.secret_set ? "••••••• (lascia vuoto per non modificare)" : "Incolla il secret completo"} /></div>
          <div className="sm:col-span-2"><Label>Base URL</Label><Input className="mt-1.5 font-mono" data-testid="vediloo-baseurl-input" value={ved.base_url || ""} onChange={(e) => setVed({ ...ved, base_url: e.target.value })} placeholder="https://…/api/partner" /></div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={saveVediloo} disabled={savingVed} data-testid="vediloo-save-button">{savingVed ? "Salvataggio…" : "Salva credenziali"}</Button>
          <Button type="button" variant="outline" disabled={testingVed} onClick={testVediloo} data-testid="vediloo-test-button">{testingVed ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Test…</> : "Testa connessione"}</Button>
          {vedTest && <span className={`text-xs flex items-center gap-1 ${vedTest.ok ? "text-teal-500" : "text-red-400"}`} data-testid="vediloo-test-msg">{vedTest.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{vedTest.message || vedTest.detail}</span>}
        </div>
      </Card>
      )}

      {show("reloadly") && (
      <Card className="p-6 glow-border space-y-4" data-testid="reloadly-card">
        <div className="flex flex-wrap items-center gap-3 [&>div]:min-w-0">
          <div className="w-10 h-10 shrink-0 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Plug className="w-5 h-5" /></div>
          <div>
            <h2 className="font-head text-lg font-semibold">Reloadly — Ricariche &amp; Gift Card</h2>
            <p className="text-xs text-muted-foreground">Provider per ricariche mobili (airtime) e gift card. OAuth2 client-credentials.</p>
          </div>
          <div className="ml-auto flex flex-wrap min-w-0 max-w-full gap-2">
            <Badge variant="outline" className="uppercase">{rel.mode}</Badge>
            <Badge className={rel.configured ? "bg-teal-600 hover:bg-teal-600" : ""} variant={rel.configured ? undefined : "outline"}>{rel.configured ? "Configurato" : "Da configurare"}</Badge>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Client ID</Label><Input className="mt-1.5 font-mono" data-testid="reloadly-clientid-input" value={rel.client_id || ""} onChange={(e) => setRel({ ...rel, client_id: e.target.value })} placeholder="Reloadly Client ID" /></div>
          <div><Label className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Client Secret {rel.client_secret_set && <span className="text-xs text-teal-500">(salvato)</span>}</Label><Input type="password" className="mt-1.5 font-mono" data-testid="reloadly-secret-input" value={relSecret} onChange={(e) => setRelSecret(e.target.value)} placeholder={rel.client_secret_set ? "••••••• (lascia vuoto per non modificare)" : "Client Secret"} /></div>
          <div><Label className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Webhook Secret {rel.webhook_secret_set && <span className="text-xs text-teal-500">(salvato)</span>}</Label><Input type="password" className="mt-1.5 font-mono" data-testid="reloadly-webhook-input" value={relHook} onChange={(e) => setRelHook(e.target.value)} placeholder={rel.webhook_secret_set ? "••••••• (lascia vuoto per non modificare)" : "Webhook signing secret"} /></div>
          <div className="sm:col-span-2"><Label>URL Webhook (configurato su Reloadly)</Label><Input className="mt-1.5 font-mono" data-testid="reloadly-webhookurl-input" value={rel.webhook_url || ""} onChange={(e) => setRel({ ...rel, webhook_url: e.target.value })} placeholder="https://markooplace.com/api/webhooks/reloadly" /></div>
          <div className="flex items-center justify-between rounded-lg border border-border px-3 sm:self-end sm:h-[42px]"><Label>Modalità Live</Label><Switch checked={rel.mode === "production"} onCheckedChange={(v) => setRel({ ...rel, mode: v ? "production" : "sandbox" })} data-testid="reloadly-mode-switch" /></div>
        </div>
        <div className="flex flex-wrap gap-6">
          <div className="flex items-center gap-2 text-sm"><Switch checked={!!rel.products?.airtime} onCheckedChange={(v) => setRel({ ...rel, products: { ...rel.products, airtime: v } })} data-testid="reloadly-airtime-switch" /> Ricariche (Airtime)</div>
          <div className="flex items-center gap-2 text-sm"><Switch checked={!!rel.products?.giftcard} onCheckedChange={(v) => setRel({ ...rel, products: { ...rel.products, giftcard: v } })} data-testid="reloadly-giftcard-switch" /> Gift Card</div>
        </div>
        <div className="rounded-lg border border-border p-3 text-xs">
          <p className="text-muted-foreground mb-1">URL webhook da inserire nel dashboard Reloadly:</p>
          <code className="font-mono break-all" data-testid="reloadly-webhook-url">https://markooplace.com/api/webhooks/reloadly</code>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={saveReloadly} disabled={savingRel} data-testid="reloadly-save-button">{savingRel ? "Salvataggio…" : "Salva credenziali"}</Button>
          <Button type="button" variant="outline" disabled={testingRel} onClick={testReloadly} data-testid="reloadly-test-button">{testingRel ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Test…</> : "Testa connessione"}</Button>
          {relTest && <span className={`text-xs flex items-center gap-1 ${relTest.ok ? "text-teal-500" : "text-red-400"}`} data-testid="reloadly-test-msg">{relTest.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{relTest.message || relTest.detail}</span>}
        </div>
      </Card>
      )}

      {show("fatture") && <FicSettingsCard />}
      {show("stripe") && <StripeSettingsCard />}
      {show("venergia") && <><VenergiaKeysCard /><Card className="p-5 glow-border"><NetworkDiagnostics /></Card></>}

      {show("notifiche") && (
      <form onSubmit={save} className="space-y-6">
        <Card className="p-6 glow-border space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><Mail className="w-5 h-5" /></div>
            <div>
              <h2 className="font-head text-lg font-semibold">Email (Resend)</h2>
              <p className="text-xs text-muted-foreground">Servizio email gestito — nessuna chiave richiesta.</p>
            </div>
            <Badge className="ml-auto bg-teal-600 hover:bg-teal-600">Attivo</Badge>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div><Label>Notifiche email ai clienti</Label><p className="text-xs text-muted-foreground mt-0.5">Invia email di conferma per ricariche e attivazioni.</p></div>
            <Switch checked={form.email_notifications_enabled} onCheckedChange={(v) => setForm({ ...form, email_notifications_enabled: v })} data-testid="email-notify-switch" />
          </div>
        </Card>

        <Card className="p-6 glow-border space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><MessageSquare className="w-5 h-5" /></div>
            <div>
              <h2 className="font-head text-lg font-semibold">SMS (Twilio)</h2>
              <p className="text-xs text-muted-foreground">Inserisci le credenziali del tuo account Twilio.</p>
            </div>
            <Badge variant="outline" className="ml-auto">{tokenSet && form.twilio_account_sid ? "Configurato" : "Da configurare"}</Badge>
          </div>
          <div>
            <Label>Account SID</Label>
            <Input className="mt-1.5 font-mono" data-testid="twilio-sid-input" value={form.twilio_account_sid}
              onChange={(e) => setForm({ ...form, twilio_account_sid: e.target.value })} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
          </div>
          <div>
            <Label className="flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Auth Token {tokenSet && <span className="text-xs text-teal-500">(salvato — lascia vuoto per non modificare)</span>}</Label>
            <Input type="password" className="mt-1.5 font-mono" data-testid="twilio-token-input" value={form.twilio_auth_token}
              onChange={(e) => setForm({ ...form, twilio_auth_token: e.target.value })} placeholder={tokenSet ? "••••••••••••••••" : "Il tuo Auth Token"} />
          </div>
          <div>
            <Label>Numero mittente</Label>
            <Input className="mt-1.5 font-mono" data-testid="twilio-from-input" value={form.twilio_from_number}
              onChange={(e) => setForm({ ...form, twilio_from_number: e.target.value })} placeholder="+391234567890" />
          </div>
          <div>
            <Label>Verify Service SID <span className="text-xs text-muted-foreground">(per gli OTP di firma contratto)</span></Label>
            <Input className="mt-1.5 font-mono" data-testid="twilio-verify-input" value={form.twilio_verify_service_sid}
              onChange={(e) => setForm({ ...form, twilio_verify_service_sid: e.target.value })} placeholder="VAxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div><Label>Notifiche SMS ai clienti</Label><p className="text-xs text-muted-foreground mt-0.5">Richiede credenziali Twilio valide.</p></div>
            <Switch checked={form.sms_enabled} onCheckedChange={(v) => setForm({ ...form, sms_enabled: v })} data-testid="sms-enabled-switch" />
          </div>
        </Card>

        <Card className="p-6 glow-border space-y-4" data-testid="twofa-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><ShieldCheck className="w-5 h-5" /></div>
            <div>
              <h2 className="font-head text-lg font-semibold">Verifica in due passaggi (2FA)</h2>
              <p className="text-xs text-muted-foreground">Richiedi un codice OTP all'accesso per i ruoli selezionati. SMS via Twilio Verify con fallback email. Predefinito: disattivo.</p>
            </div>
          </div>
          <div className="space-y-2">
            {[{ v: "admin", l: "Admin" }, { v: "dealer", l: "Dealer (incl. Master Dealer)" }, { v: "agent", l: "Agente" }, { v: "support", l: "Support" }, { v: "customer", l: "Cliente" }].map((r) => (
              <div key={r.v} className="flex items-center justify-between rounded-lg border border-border p-3">
                <Label>{r.l}</Label>
                <Switch data-testid={`twofa-switch-${r.v}`} checked={(form.twofa_roles || []).includes(r.v)}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, twofa_roles: v ? [...(f.twofa_roles || []), r.v] : (f.twofa_roles || []).filter((x) => x !== r.v) }))} />
              </div>
            ))}
          </div>
        </Card>

        <Button type="submit" disabled={saving} data-testid="integrations-save-button">{saving ? "Salvataggio…" : "Salva impostazioni"}</Button>
      </form>
      )}
    </div>
  );
}
